/**
 * Creates an adaptive debounce function that adjusts delay based on update frequency
 *
 * @param {Function} callback - The function to debounce
 * @param {Object} options - Configuration options
 * @param {number} options.normalDelay - Delay for normal updates (default: 1000ms)
 * @param {number} options.bulkDelay - Delay for bulk operations (default: 3000ms)
 * @param {number} options.rapidThreshold - Time window to detect rapid updates (default: 200ms)
 * @param {number} options.bulkThreshold - Number of rapid updates to trigger bulk delay (default: 4)
 * @param {string} options.debugLabel - Label for debug logging (optional)
 * @returns {Function} The debounced function
 */
export function createAdaptiveDebounce(callback, options = {}) {
  const {
    normalDelay = 1000,
    bulkDelay = 3000,
    rapidThreshold = 200,
    bulkThreshold = 4,
    debugLabel = ''
  } = options;

  let debounceTimer = null;
  let updateCount = 0;
  let lastUpdateTime = Date.now();
  let firstUpdateTime = null;

  const debouncedFn = () => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTime;
    lastUpdateTime = now;

    // Track when the update burst started
    if (firstUpdateTime === null) {
      firstUpdateTime = now;
    }

    // If updates are coming rapidly (within rapidThreshold of each other), it's likely a bulk operation
    if (timeSinceLastUpdate < rapidThreshold) {
      updateCount++;
    } else {
      updateCount = 0; // Reset if updates slow down
      firstUpdateTime = now;
    }

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Adaptive delay: much longer for bulk operations (detected by rapid updates)
    // If we've had bulkThreshold+ rapid updates, wait bulkDelay after the LAST update
    const delay = updateCount > bulkThreshold ? bulkDelay : normalDelay;

    debounceTimer = setTimeout(() => {
      callback();
      updateCount = 0; // Reset counter after processing
      firstUpdateTime = null;
    }, delay);
  };

  // Add a cancel method to clear pending debounce
  debouncedFn.cancel = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
      updateCount = 0;
      firstUpdateTime = null;
    }
  };

  return debouncedFn;
}
